import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import useChangePhoto from '@/hooks/useChangePhoto';
import useGetProfile from '@/hooks/useGetProfile';
import useUpdateProfile from '@/hooks/useUpdateProfile';
import { Link, Mail, Phone } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import AvatarUploader from './AvatarUploader';
import AgentUpdateModal from './ui/AgentUpdateModal';

const AgentProfileForm = () => {
  const [file, setFile] = useState<File | null>(null);
  const [profileImage, setProfileImage] = useState<string | null>(null);
  const [agentData, setAgentData] = useState({
    firstName: '',
    lastName: '',
    companyName: '',
    licenseNumber: '',
    phoneNumber: '',
    emailAddress: '',
    website: '',
  });
  const [showReminder, setShowReminder] = useState(false);

  const { data: profileData, isLoading } = useGetProfile();
  const { mutate: updateProfile } = useUpdateProfile();

  useEffect(() => {
    if (profileData && !isLoading) {
      setAgentData({
        firstName: profileData.firstName || '',
        lastName: profileData.lastName || '',
        companyName: profileData.companyName || '',
        licenseNumber: profileData.licenseNumber || '',
        phoneNumber: profileData.phoneNumber || '',
        emailAddress: profileData.emailAddress || '',
        website: profileData.website || '',
      });
      if (profileData.photoUrl) {
        setProfileImage(profileData.photoUrl);
      }
    }
  }, [isLoading, profileData]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setAgentData({
      ...agentData,
      [name]: value,
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateProfile(agentData, {
      onSuccess: () => {
        toast.success('Profile saved successfully');
      },
    });
  };

  const handleCopyLink = () => {
    const url = `${window.location.origin}?agent=${profileData.identifier}`;

    navigator.clipboard
      .writeText(url)
      .then(() => {
        toast.success('Copied to clipboard!');
      })
      .catch((err) => {
        console.error('Failed to copy: ', err);
      });
  };

  useEffect(() => {
    if (
      !profileData.licenseNumber ||
      !profileData.phoneNumber ||
      !profileData.companyName
    ) {
      setShowReminder(true);
    }
  }, [
    profileData.companyName,
    profileData.licenseNumber,
    profileData.phoneNumber,
  ]);

  console.log(profileData);

  return (
    <Card className="w-full">
      <AgentUpdateModal
        onClose={() => setShowReminder(false)}
        show={showReminder}
      />
      <CardHeader>
        <CardTitle className="text-2xl text-tax-deepBlue">
          Agent Information
        </CardTitle>
        <CardDescription>
          Update your profile information and upload a professional photo
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="flex flex-col md:flex-row gap-6">
            {/* <div className="flex flex-col items-center space-y-4">
              <Avatar className="w-32 h-32 border-2 border-tax-borderGray">
                <AvatarImage src={profileImage || '/placeholder.svg'} />
                <AvatarFallback className="bg-tax-actionOrange text-white text-2xl">
                  <User />
                </AvatarFallback>
              </Avatar>

              <Dialog>
                <DialogTrigger asChild>
                  <Button variant="outline" size="sm" className="flex gap-2">
                    <Image className="h-4 w-4" />
                    Edit Photo
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle>Upload Profile Picture</DialogTitle>
                    <DialogDescription>
                      Upload a professional photo for your profile. You can crop
                      and resize after selecting.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="flex flex-col items-center justify-center space-y-4 py-4">
                    {profileImage && (
                      <div className="relative w-48 h-48 mb-4">
                        <img
                          src={profileImage}
                          alt="Profile Preview"
                          className="rounded-full object-cover w-full h-full border-2 border-tax-borderGray"
                        />
                      </div>
                    )}
                    <div className="flex items-center justify-center w-full">
                      <label
                        htmlFor="profile-upload"
                        className="flex flex-col items-center justify-center w-full h-32 border-2 border-tax-borderGray border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100"
                      >
                        <div className="flex flex-col items-center justify-center pt-5 pb-6">
                          <Upload className="w-8 h-8 mb-2 text-gray-500" />
                          <p className="mb-2 text-sm text-gray-500">
                            <span className="font-semibold">
                              Click to upload
                            </span>{' '}
                            or drag and drop
                          </p>
                          <p className="text-xs text-gray-500">
                            PNG, JPG or WEBP (MAX. 2MB)
                          </p>
                        </div>
                        <input
                          id="profile-upload"
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={handleImageChange}
                        />
                      </label>
                    </div>
                  </div>
                  <DialogFooter className="flex justify-between">
                    <Button variant="outline" type="button">
                      Cancel
                    </Button>
                    <Button
                      disabled={uploadPending}
                      type="button"
                      onClick={handleChangePhoto}
                    >
                      Save Changes
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div> */}
            <AvatarUploader
              profileImage={profileImage || '/placeholder.svg'}
              onSuccess={(data) =>
                updateProfile({
                  photoUrl: data.url,
                })
              }
            />

            <div className="flex-1 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="fullName">Full Name</Label>
                <div className="flex items-center gap-4 flex-1">
                  <div className="flex items-center flex-1">
                    <Input
                      id="firstName"
                      name="firstName"
                      value={agentData.firstName}
                      onChange={handleChange}
                      placeholder="Your first name"
                    />
                  </div>
                  <div className="flex items-center flex-1">
                    <Input
                      id="lastName"
                      name="lastName"
                      value={agentData.lastName}
                      onChange={handleChange}
                      placeholder="Your last name"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="companyName">Company Name</Label>
                <Input
                  id="companyName"
                  name="companyName"
                  value={agentData.companyName}
                  onChange={handleChange}
                  placeholder="Your company name"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="licenseNumber">License Number</Label>
                <Input
                  id="licenseNumber"
                  name="licenseNumber"
                  value={agentData.licenseNumber}
                  onChange={handleChange}
                  placeholder="Your real estate license number"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="phoneNumber">Cell Phone</Label>
                  <div className="flex items-center">
                    <Phone className="w-4 h-4 mr-2 text-tax-neutralGray" />
                    <Input
                      id="phoneNumber"
                      name="phoneNumber"
                      value={agentData.phoneNumber}
                      onChange={handleChange}
                      placeholder="(123) 456-7890"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <div className="flex items-center">
                    <Mail className="w-4 h-4 mr-2 text-tax-neutralGray" />
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={agentData.emailAddress}
                      onChange={handleChange}
                      placeholder="your@email.com"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="website">Website</Label>
                  <div className="flex items-center">
                    <Link className="w-4 h-4 mr-2 text-tax-neutralGray" />
                    <Input
                      id="website"
                      name="website"
                      value={agentData.website}
                      onChange={handleChange}
                      placeholder="https://yoursite.com"
                    />
                  </div>
                </div>
              </div>

              <div className="flex flex-col md:flex-row items-end gap-4">
                <div className="space-y-2 flex-1 w-full">
                  <Label htmlFor="identifier">My tax appeal link</Label>
                  <Input
                    id="identifier"
                    name="identifier"
                    value={`${window.location.origin}?agent=${profileData.identifier}`}
                    disabled
                  />
                </div>
                <Button
                  type="button"
                  onClick={handleCopyLink}
                  className="bg-primary hover:bg-primary/90 w-full md:w-fit"
                >
                  Copy my link
                </Button>
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-4">
            <Button
              type="submit"
              className="bg-primary hover:bg-primary/90 w-full md:w-fit"
            >
              Save Profile
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default AgentProfileForm;
